import React, { useState } from 'react';
import { Copy, CheckCircle2, Users, ArrowRightLeft, Mail } from 'lucide-react';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { DjayNarrator } from '../../../mascot';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
interface InviteCoTeacherPanelProps {
  open: boolean;
  onClose: () => void;
  classroom: Classroom;
  activeAccount: TeacherAccount;
  /** All teachers known to the active account, used to render names of existing co-teachers. */
  teacherById: Record<string, TeacherAccount | undefined>;
  /** Owner-only: open the transfer-ownership confirmation. */
  onRequestTransfer?: (newOwnerId: string) => void;
}
/**
 * Owner-facing panel to invite, list, and manage co-teachers for a classroom.
 *
 * Wave 3.2: We reuse the classroom's existing `joinCode`. The receiving teacher
 * enters this code in the "Ik heb een co-docent-code" flow (Wave 3.3) to be
 * added to `coTeacherIds`. No separate temporary code is generated yet.
 *
 * Djay-zone: small bubble on empty co-teacher list (Zone 3).
 */
export function InviteCoTeacherPanel({
  open,
  onClose,
  classroom,
  activeAccount,
  teacherById,
  onRequestTransfer
}: InviteCoTeacherPanelProps) {
  const [copied, setCopied] = useState(false);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const coTeachers = classroom.coTeacherIds ?? [];
  const canAddMore = coTeachers.length < MAX_CO_TEACHERS_PER_CLASSROOM;
  const isOwner = classroom.teacherId === activeAccount.id;
  const handleCopy = () => {
    navigator.clipboard.writeText(classroom.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <>
      <SlideOver open={open} onClose={onClose} title="Co-docent uitnodigen" description={`Voor klas: ${classroom.name}`} width="md" data-id="element-8628">
        <div className="p-6 space-y-6" data-id="element-8629">
          {/* Djay intro */}
          <div className="flex items-start gap-3 p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl" data-id="element-8630">
            <DjayNarrator size="xs" emotion="encouraging" data-id="element-8631" />
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-8632">
              Werk je samen met een collega aan deze klas? Geef hen de klascode
              hieronder. Een co-docent kan leerlingen begeleiden en huiswerk
              uitzetten, maar alleen jij blijft eigenaar.
            </p>
          </div>

          {/* Invite code */}
          {isOwner && <div data-id="element-8633">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8634">
                Klascode delen
              </h3>
              {canAddMore ? <div className="p-4 bg-white border-2 border-dashed border-indigo-200 rounded-2xl" data-id="element-8635">
                  <div className="flex items-center justify-between gap-3" data-id="element-8636">
                    <div data-id="element-8637">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8638">
                        Code
                      </p>
                      <p className="text-2xl font-mono font-black text-slate-900 tracking-widest" data-id="element-8639">
                        {classroom.joinCode}
                      </p>
                    </div>
                    <Button onClick={handleCopy} variant="secondary" size="sm" className="flex items-center gap-2" data-id="element-8640">
                      {copied ? <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-8641" />
                          Gekopieerd
                        </> : <>
                          <Copy className="w-4 h-4" data-id="element-8642" />
                          Kopieer
                        </>}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-id="element-8643">
                    Je collega voert deze code in via{' '}
                    <span className="font-bold" data-id="element-8644">
                      Klas Beheer → "Ik heb een co-docent-code"
                    </span>{' '}
                    in hun eigen docentportaal.
                  </p>
                </div> : <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl" data-id="element-8645">
                  <p className="text-sm font-bold text-amber-900" data-id="element-8646">
                    Maximum bereikt
                  </p>
                  <p className="text-xs text-amber-800 mt-1" data-id="element-8647">
                    Deze klas heeft al {MAX_CO_TEACHERS_PER_CLASSROOM}{' '}
                    co-docenten. Verwijder eerst een co-docent om iemand anders
                    toe te voegen.
                  </p>
                </div>}
            </div>}

          {/* E-mail invite (placeholder for Wave 5) */}
          {isOwner && <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-id="element-8648">
              <div className="flex items-center gap-2 mb-1" data-id="element-8649">
                <Mail className="w-4 h-4 text-slate-400" data-id="element-8650" />
                <p className="text-sm font-bold text-slate-700" data-id="element-8651">
                  Uitnodigen per e-mail
                </p>
                <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded" data-id="element-8652">
                  Binnenkort
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed" data-id="element-8653">
                E-mailuitnodigingen komen beschikbaar zodra het school-account
                aan een backend is gekoppeld.
              </p>
            </div>}

          {/* Current teachers */}
          <div data-id="element-8654">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8655">
              Huidige docenten
            </h3>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-8656">
              {/* Owner */}
              <li className="flex items-center justify-between px-4 py-3" data-id="element-8657">
                <div className="flex items-center gap-3 min-w-0" data-id="element-8658">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-8659">
                    {(teacherById[classroom.teacherId]?.displayName ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0" data-id="element-8660">
                    <p className="text-sm font-bold text-slate-900 truncate" data-id="element-8661">
                      {teacherById[classroom.teacherId]?.displayName ?? 'Onbekend'}
                      {classroom.teacherId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-8662">
                          (jij)
                        </span>}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded" data-id="element-8663">
                  Eigenaar
                </span>
              </li>

              {/* Co-teachers */}
              {coTeachers.length === 0 ? <li className="px-4 py-6 text-center" data-id="element-8664">
                  <div className="flex justify-center mb-2" data-id="element-8665">
                    <Users className="w-5 h-5 text-slate-300" data-id="element-8666" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium" data-id="element-8667">
                    Nog geen co-docenten
                  </p>
                </li> : coTeachers.map(coId => {
              const co = teacherById[coId];
              const name = co?.displayName ?? 'Onbekende docent';
              return <li key={coId} className="flex items-center justify-between px-4 py-3" data-id="element-8668">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-8669">
                        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-8670">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0" data-id="element-8671">
                          <p className="text-sm font-bold text-slate-900 truncate" data-id="element-8672">
                            {name}
                            {coId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-8673">
                                (jij)
                              </span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-8674">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-700 rounded" data-id="element-8675">
                          Co-docent
                        </span>
                        {isOwner && onRequestTransfer && <button onClick={() => setTransferTarget(coId)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Eigenaarschap overdragen aan deze co-docent" data-id="element-8676">
                            <ArrowRightLeft className="w-4 h-4" data-id="element-8677" />
                          </button>}
                      </div>
                    </li>;
            })}
            </ul>
          </div>
        </div>
      </SlideOver>

      {transferTarget && onRequestTransfer && <ConfirmDialog open={true} title="Eigenaarschap overdragen?" message={`Hierna is ${teacherById[transferTarget]?.displayName ?? 'deze docent'} de nieuwe eigenaar van ${classroom.name}. Jij blijft betrokken als co-docent. Deze actie is niet ongedaan te maken.`} confirmLabel="Ja, draag over" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      onRequestTransfer(transferTarget);
      setTransferTarget(null);
      onClose();
    }} onCancel={() => setTransferTarget(null)} data-id="element-8678" />}
    </>;
}